import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search, Mail, KeyRound, Users, X, Check } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

export default function AdminAccountsPage() {
  const [role, setRole] = useState("owner");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = useCallback((r = role, search = "") => {
    setLoading(true);
    apiClient.get(`/admin/users?role=${r}${search ? `&q=${encodeURIComponent(search)}` : ""}`)
      .then((res) => setUsers(res.data)).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [role]);
  useEffect(() => { load(role, q); }, [role]); // eslint-disable-line

  const saveEdit = async () => {
    try {
      await apiClient.patch(`/admin/users/${editing.id}`, { name: editing.name, email: editing.email });
      toast.success("Akun diperbarui."); setEditing(null); load(role, q);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const saveReset = async () => {
    try {
      await apiClient.post(`/admin/users/${resetting.id}/password`, { new_password: resetting.new_password });
      toast.success("Password direset."); setResetting(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <AdminLayout testid="admin-accounts-page">
      <div className="flex items-center gap-2 mb-1"><Users size={22} className="text-primary" /><h1 className="font-heading text-3xl font-bold tracking-tight">Kelola Akun Pengguna</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Ubah email/nama dan reset password akun Owner & Customer.</p>

      <div className="flex gap-2 mb-4">
        {[["owner", "Owner"], ["customer", "Customer"]].map(([v, l]) => (
          <button key={v} onClick={() => setRole(v)} data-testid={`accounts-role-${v}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${role === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>{l}</button>
        ))}
      </div>
      <div className="relative max-w-sm mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); load(role, e.target.value); }} placeholder="Cari nama atau email..." data-testid="accounts-search" className={`${inputCls} pl-9`} />
      </div>

      {loading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div> : (
        <div className="space-y-2" data-testid="accounts-list">
          {users.map((u) => (
            <div key={u.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`account-row-${u.id}`}>
              <div className="min-w-0 flex-1"><p className="font-semibold truncate">{u.name}</p><p className="text-xs text-muted-foreground truncate">{u.email}</p></div>
              <button onClick={() => setEditing({ id: u.id, name: u.name, email: u.email })} data-testid={`account-edit-${u.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><Mail size={13} /> Edit</button>
              <button onClick={() => setResetting({ id: u.id, email: u.email, new_password: "" })} data-testid={`account-reset-${u.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><KeyRound size={13} /> Reset PW</button>
            </div>
          ))}
          {users.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada akun.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="account-edit-modal">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Edit Akun</h3><button onClick={() => setEditing(null)}><X size={16} /></button></div>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nama" className={inputCls} data-testid="account-edit-name" />
            <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="account-edit-email" />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold">Batal</button>
              <button onClick={saveEdit} data-testid="account-edit-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Check size={15} /> Simpan</button></div>
          </div>
        </div>
      )}
      {resetting && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="account-reset-modal">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Reset Password</h3><button onClick={() => setResetting(null)}><X size={16} /></button></div>
            <p className="text-sm text-muted-foreground">Untuk: <span className="font-medium text-foreground">{resetting.email}</span></p>
            <input type="password" value={resetting.new_password} onChange={(e) => setResetting({ ...resetting, new_password: e.target.value })} placeholder="Password baru (min. 8, huruf & angka)" className={inputCls} data-testid="account-reset-input" />
            <div className="flex justify-end gap-2"><button onClick={() => setResetting(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold">Batal</button>
              <button onClick={saveReset} data-testid="account-reset-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><KeyRound size={15} /> Reset</button></div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
